import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@bi-studio/shared/hooks';
import { FormDisplay } from '@/components/forms';
import {
    LoadingOverlay,
    useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { AlertCircle } from 'lucide-react';
import { submitForm, getFolderForItem, deleteForm, getPublicForm, submitPublicForm } from '@bi-studio/shared/apis';
import { AppLayout, LoadingScreen, ConfirmationModal, NotFoundState } from '@bi-studio/shared/components';
import { PageActions, FormActionsButton, PageContainer, ItemDetailModal } from '@/components';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
    fetchFormDetails, 
    selectCurrentForm, 
    selectFormLoading, 
    resetFormState 
} from '@/store/slices/formSlice';
import type { Form } from '@bi-studio/shared/lib';

export default function FormDetailPage() {
    const { token, isLoading: isAuthLoading } = useAuth();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const isFullscreen = searchParams.get("fullscreen") === "true";
    const isEmbed = searchParams.get("embed") === "true";

    const handleLogout = useCallback(() => {
        navigate("/login");
    }, [navigate]);

    const form = useAppSelector(selectCurrentForm);
    const loading = useAppSelector(selectFormLoading);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { setColorScheme } = useMantineColorScheme();

    useEffect(() => {
        const theme = searchParams.get("theme");
        if (theme === "dark" || theme === "light" || theme === "auto") {
            setColorScheme(theme);
        }
    }, [searchParams, setColorScheme]);

    const mantineForm = useForm<Record<string, any>>({
        initialValues: {},
        validate: (values) => {
            if (!form) return {};
            const errors: Record<string, string> = {};
            form.fields.forEach((field) => {
                if (field.required) {
                    const value = values[field.name];
                    if (value === undefined || value === null || value === '' ||
                        (field.type === 'checkbox' && value === false) ||
                        (field.type === 'multiselect' && Array.isArray(value) && value.length === 0)) {
                        errors[field.name] = `${field.label} is required`;
                    }
                }
            });
            return errors;
        },
    });

    const initializeFormValues = useCallback((fetchedForm: Form) => {
        const initialValues: Record<string, any> = {};
        fetchedForm.fields.forEach((field) => {
            if (field.type === 'checkbox') {
                initialValues[field.name] = false;
            } else if (field.type === 'number' || field.type === 'date') {
                initialValues[field.name] = null;
            } else if (field.type === 'multiselect') {
                initialValues[field.name] = [];
            } else {
                initialValues[field.name] = '';
            }
        });
        mantineForm.setInitialValues(initialValues);
        mantineForm.setValues(initialValues);
    }, [mantineForm]);

    const loadFormData = useCallback(() => {
        if (!id || isAuthLoading) return;

        if (token) {
            dispatch(fetchFormDetails({ token, formId: id }))
                .unwrap()
                .then(initializeFormValues);
        } else {
            getPublicForm(id)
                .then(initializeFormValues);
        }
    }, [id, token, isAuthLoading, dispatch, initializeFormValues]);

    useEffect(() => {
        loadFormData();
        return () => {
            dispatch(resetFormState());
        };
    }, [id, token, isAuthLoading, dispatch]);

    useEffect(() => {
        if (!form) return;

        const config = (form.config as Record<string, any>) || {};
        const bgImage = config.backgroundImage;

        if (bgImage && isFullscreen) {
            setImageLoaded(false);
            const img = new Image();
            img.src = bgImage;
            img.onload = () => setImageLoaded(true);
            img.onerror = () => setImageLoaded(true);
        } else {
            setImageLoaded(true);
        }
    }, [form, isFullscreen]);

    const handleBack = useCallback(async () => {
        if (!token || !id) {
            navigate(-1);
            return;
        }

        try {
            const folderInfo = await getFolderForItem(token, "form", id);
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            console.error("Failed to get folder for form:", err);
            navigate(-1);
        }
    }, [token, id, navigate]);

    const onConfirmDelete = async () => {
        if (!token || !id) return;

        try {
            setIsDeleting(true);
            await deleteForm(token, id);
            notifications.show({
                title: 'Success',
                message: 'Form deleted successfully',
                color: 'green',
            });
            setIsDeleteModalOpen(false);
            const folderId = searchParams.get("folderId");
            navigate(folderId ? `/folders/${folderId}` : "/folders");
        } catch (err) {
            notifications.show({
                title: 'Error',
                message: err instanceof Error ? err.message : 'Failed to delete form',
                color: 'red',
            });
            setIsDeleteModalOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSubmit = async (values: typeof mantineForm.values) => {
        if (!id || !form) return;

        const isFormIncomplete = form.fields.some(field =>
            field.required && (
                mantineForm.values[field.name] === undefined ||
                mantineForm.values[field.name] === null ||
                mantineForm.values[field.name] === '' ||
                (field.type === 'checkbox' && mantineForm.values[field.name] === false) ||
                (field.type === 'multiselect' && Array.isArray(mantineForm.values[field.name]) && mantineForm.values[field.name].length === 0)
            )
        );

        if (isFormIncomplete) return;

        const cleanedData = { ...values };
        form.fields.forEach(field => {
            if (cleanedData[field.name] === '') {
                cleanedData[field.name] = null;
            }
        });

        setSubmitting(true);
        try {
            if (token) {
                await submitForm(token, id, cleanedData);
            } else {
                await submitPublicForm(id, cleanedData);
            }
            mantineForm.reset();
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to submit form',
                color: 'red',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const config = form ? (form.config as Record<string, any>) || {} : {};
    const hasBgImage = !!config.backgroundImage;
    const isImageLoading = isFullscreen && hasBgImage && !imageLoaded;

    const isReady = !!form;
    const isPageLoading = loading || isAuthLoading;

    if (!isReady && isPageLoading) {
        return (
            <AppLayout onLogout={handleLogout} hideHeader={!token}>
                <LoadingScreen message="Loading form..." />
            </AppLayout>
        );
    }

    if (!form) {
        const fallback = (
            <NotFoundState
                title="Form Unavailable"
                message="This form is either private, does not exist, or you don't have permission to view it."
                icon={<AlertCircle size={32} />}
                showAction={!isEmbed}
                actionLabel="Back to Folders"
                onAction={() => navigate('/folders')}
            />
        );
        return isEmbed ? fallback : <AppLayout onLogout={handleLogout} hideHeader={!token}>{fallback}</AppLayout>;
    }

    const content = (
        <PageContainer className={isEmbed ? "p-2" : ""}>
            <LoadingOverlay visible={(loading && !!form) || isImageLoading} overlayProps={{ blur: 0, opacity: 1, color: 'var(--mantine-color-body)' }} zIndex={100} />
            {!isFullscreen && (
                <PageActions title={form.name} onBack={!isEmbed ? handleBack : undefined}>
                    {!isEmbed && (
                        <FormActionsButton
                            setIsDetailsOpen={setIsDetailsOpen}
                            setIsDeleteModalOpen={setIsDeleteModalOpen}
                            onViewFullscreen={() => navigate(`/forms/${id}/fullscreen`)}
                            onViewSteps={() => navigate(`/forms/${id}/steps`)}
                            onEdit={() => navigate(`/forms/${id}/edit`)}
                        />
                    )}
                </PageActions>
            )}
            <FormDisplay
                form={form}
                mantineForm={mantineForm}
                submitting={submitting}
                onSubmit={handleSubmit}
                handleValidationError={() => console.log('Validation failed')}
            />

            <ItemDetailModal
                opened={isDetailsOpen}
                onClose={() => {
                    setIsDetailsOpen(false);
                    loadFormData();
                }}
                itemType="form"
                itemId={id ?? null}
            />

            <ConfirmationModal
                opened={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={onConfirmDelete}
                title="Delete Form"
                confirmLabel="Delete"
                confirmColor="red"
                loading={isDeleting}
            >
                {`Are you sure you want to delete "${form.name}"? This action cannot be undone.`}
            </ConfirmationModal>
        </PageContainer>
    );

    return isEmbed ? content : (
        <AppLayout
            onLogout={handleLogout}
            hideHeader={isFullscreen || !token}
            subtitle="Workspace"
        >
            {content}
        </AppLayout>
    );
}
